import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const UserContext = createContext({
    user: null,
    loading: false,
    error: '',
    refreshUser: async () => {},
    updateUser: () => {},
    resetUser: () => {},
});

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const location = useLocation();
    const fetchUser = useCallback(async () => {
        const token = localStorage.getItem('token');
        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/auth/me', {
                credentials: 'same-origin',
                headers: token ? { 'x-auth-token': token } : {},
            });

            if (response.status === 401) {
                localStorage.removeItem('token');
                setUser(null);
                setError('');
                return;
            }

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || 'Failed to load user profile.');
            }

            const data = await response.json();
            setUser(data);
            // Sync server-stored ad consent to localStorage so client ad components can read it immediately
            if (typeof window !== 'undefined' && data && (data.adConsent === 'granted' || data.adConsent === 'denied')) {
                try { localStorage.setItem('ads_consent', data.adConsent); } catch (e) {}
            }
        } catch (err) {
            setError(err.message);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateUser = useCallback((updates) => {
        setUser(prev => (prev ? { ...prev, ...updates } : prev));
    }, []);

    const resetUser = useCallback(() => {
        setUser(null);
        setLoading(false);
        setError('');
    }, []);

    const shouldAttemptFetch = !['/', '/login', '/register'].includes(location.pathname);

    useEffect(() => {
        if (shouldAttemptFetch) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [fetchUser, shouldAttemptFetch]);

    const value = {
        user,
        loading,
        error,
        refreshUser: fetchUser,
        updateUser,
        resetUser,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
